import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { BuilderSection, WebsiteBuilderState } from 'types/websiteBuilder';
import {
  BUILDER_STORAGE_KEY,
  createDefaultWebsiteBuilderState,
  createSection,
} from './builderTemplates';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';
import Canvas from './Canvas';
import styles from './WebsiteBuilder.module.scss';
import { syncHeaderNavigationWithPages } from './pageNavSync';

function isWebsiteBuilderState(x: unknown): x is WebsiteBuilderState {
  if (typeof x !== 'object' || x == null) return false;
  const r = x as Record<string, unknown>;
  return Array.isArray(r.sections) && typeof r.pageBackgroundUrl === 'string';
}

function loadFromStorage(
  defaultState: WebsiteBuilderState,
): WebsiteBuilderState {
  try {
    const raw = localStorage.getItem(BUILDER_STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw) as unknown;

    // Back-compat: some versions stored just sections (array).
    if (Array.isArray(parsed)) {
      return syncHeaderNavigationWithPages({
        ...defaultState,
        sections: parsed as BuilderSection[],
      });
    }

    if (isWebsiteBuilderState(parsed)) {
      return syncHeaderNavigationWithPages({
        sections: parsed.sections,
        pageBackgroundUrl: parsed.pageBackgroundUrl,
        pages: Array.isArray(parsed.pages) ? parsed.pages : [],
      });
    }

    return defaultState;
  } catch {
    return defaultState;
  }
}

function PublicWebsiteHomePage() {
  const defaultState = useMemo(() => createDefaultWebsiteBuilderState(), []);
  const [state, setState] = useState<WebsiteBuilderState>(defaultState);

  const load = useCallback(() => {
    setState(loadFromStorage(defaultState));
  }, [defaultState]);

  useEffect(() => {
    load();
  }, [load]);

  const sections = state.sections ?? [];
  const hasCustomPageBackground = state.pageBackgroundUrl.trim() !== '';
  const effectivePageBackgroundUrl = hasCustomPageBackground
    ? state.pageBackgroundUrl
    : '';

  return (
    <div
      className={styles.container}
      style={{
        // `WebsiteBuilder.module.scss` subtracts 60px for the app global header.
        // `/membership/home` intentionally hides that global header, so we
        // need a full-viewport container height to avoid a white strip.
        height: '100vh',
        background: 'hsl(220 15% 18%)',
      }}
    >
      <main
        className={styles.canvasArea}
        style={
          hasCustomPageBackground
            ? {
                backgroundImage: `url(${effectivePageBackgroundUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {
                backgroundImage: `linear-gradient(hsl(0 0% 35% / 0.55), hsl(0 0% 35% / 0.55)), url(${heroPlaceholder})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundBlendMode: 'saturation',
              }
        }
      >
        <Canvas
          sections={sections.length > 0 ? sections : [createSection('body', 0)]}
          previewMode
          onSelectSection={() => undefined}
          onUpdateSectionData={() => undefined}
        />
      </main>
    </div>
  );
}

export default PublicWebsiteHomePage;
